// SRS (Spaced Repetition System) Service
// Implements SM-2+ algorithm with speed penalties and difficulty adjustments

import { doc, getDoc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';

class SRSService {
  constructor() {
    this.config = {
      ease_min: 1.3,
      first_intervals: [1, 6],
      speed_p80_penalty: true
    };
  }

  /**
   * SM-2+ Algorithm Implementation
   * @param {Object} reviewData - Review input data
   * @returns {Object} Updated SRS state
   */
  sm2plus(reviewData) {
    const {
      ease = 2.5,           // EF, start ~2.5
      intervalDays = 0,     // last interval; 0 if new
      reps = 0,             // successful reviews count
      latencyMs = 0,        // current answer latency
      p80LatencyMs = 30000, // per-item or per-concept p80 baseline (30 seconds default)
      correct = false,      // was the answer correct?
      difficulty = 'medium' // concept difficulty for prior adjustment
    } = reviewData;

    // Map outcome to grade 0..5
    let grade = correct ? 4 : 1;
    
    // Speed adjustments
    if (correct && latencyMs < p80LatencyMs * 0.5) grade = 5; // Very fast
    else if (correct && latencyMs < p80LatencyMs) grade = 4;  // Fast
    else if (correct && latencyMs > p80LatencyMs) grade = 3;  // Slow but correct

    // Speed penalty: if slower than p80 and not already penalized
    if (this.config.speed_p80_penalty && latencyMs > p80LatencyMs && grade > 0) {
      grade = Math.max(0, grade - 1);
    }

    // Difficulty prior adjustment (seed new concepts with lower ease if known difficult)
    let adjustedEase = ease;
    if (reps === 0 && difficulty === 'hard') {
      adjustedEase = Math.max(this.config.ease_min, ease - 0.2);
    }

    // Ease update
    const delta = 0.1 - (5 - grade) * (0.08 + (5 - grade) * 0.02);
    const newEase = Math.max(this.config.ease_min, adjustedEase + delta);

    // Interval update
    let newIntervalDays = 0;
    let newReps = reps;
    
    if (grade < 3) {
      newReps = 0;              // reset on fail
      newIntervalDays = this.config.first_intervals[0]; // 1 day
    } else {
      newReps = reps + 1;
      if (newReps === 1) {
        newIntervalDays = this.config.first_intervals[0]; // 1 day
      } else if (newReps === 2) {
        newIntervalDays = this.config.first_intervals[1]; // 6 days
      } else {
        newIntervalDays = Math.round(intervalDays * newEase);
      }
    }

    return {
      ease: newEase,
      intervalDays: newIntervalDays,
      reps: newReps,
      grade: grade,
      nextReview: this.calculateNextReview(newIntervalDays)
    };
  }

  /**
   * Calculate next review date
   * @param {number} intervalDays - Interval in days
   * @returns {Date} Next review date
   */
  calculateNextReview(intervalDays) {
    const nextReview = new Date();
    nextReview.setDate(nextReview.getDate() + intervalDays);
    return nextReview;
  }

  /**
   * Get due reviews for a user
   * @param {Object} userMastery - User's mastery data
   * @param {number} limit - Maximum number of reviews to return
   * @returns {Array} Array of due review concepts
   */
  getDueReviews(userMastery, limit = 8) {
    const now = new Date();
    const dueReviews = [];

    Object.keys(userMastery).forEach(conceptId => {
      const mastery = userMastery[conceptId];
      
      if (mastery.nextReview && mastery.nextReview.toDate() <= now) {
        dueReviews.push({
          conceptId: conceptId,
          mastery: mastery,
          daysOverdue: Math.floor((now - mastery.nextReview.toDate()) / (1000 * 60 * 60 * 24))
        });
      }
    });

    // Sort by days overdue (most overdue first) and limit
    return dueReviews
      .sort((a, b) => b.daysOverdue - a.daysOverdue)
      .slice(0, limit);
  }

  /**
   * Update mastery after a review
   * @param {string} userId - User ID
   * @param {string} conceptId - Concept ID
   * @param {Object} reviewResult - Result from sm2plus()
   * @param {number} latencyMs - Response time in milliseconds
   * @param {boolean} correct - Was the answer correct?
   */
  async updateMastery(userId, conceptId, reviewResult, latencyMs, correct) {
    try {
      const userProgressRef = doc(db, 'userProgress', userId);
      
      // Get current mastery data
      const userProgressDoc = await getDoc(userProgressRef);
      const userProgress = userProgressDoc.data();
      
      if (!userProgress.mastery) {
        userProgress.mastery = {};
      }

      // Update mastery for this concept
      const currentMastery = userProgress.mastery[conceptId] || {
        ease: 2.5,
        intervalDays: 0,
        reps: 0,
        reviewCount: 0,
        errorCount: 0,
        totalTime: 0,
        averageLatency: 0,
        p80Latency: 30000
      };

      // Update SRS state
      const updatedMastery = {
        ...currentMastery,
        ease: reviewResult.ease,
        intervalDays: reviewResult.intervalDays,
        reps: reviewResult.reps,
        lastReviewed: new Date(),
        nextReview: reviewResult.nextReview,
        reviewCount: currentMastery.reviewCount + 1,
        errorCount: currentMastery.errorCount + (correct ? 0 : 1),
        totalTime: currentMastery.totalTime + latencyMs
      };

      // Update average latency
      const totalReviews = updatedMastery.reviewCount;
      updatedMastery.averageLatency = (
        (currentMastery.averageLatency * (totalReviews - 1) + latencyMs) / totalReviews
      );

      // Update p80 latency (simplified - in production, use proper percentile calculation)
      if (latencyMs > updatedMastery.p80Latency) {
        updatedMastery.p80Latency = Math.min(updatedMastery.p80Latency * 1.1, latencyMs);
      }

      // Update user progress
      userProgress.mastery[conceptId] = updatedMastery;

      // Update rolling accuracy
      if (!userProgress.performance.rollingAccuracy[conceptId]) {
        userProgress.performance.rollingAccuracy[conceptId] = {
          accuracy: 0,
          lastUpdated: new Date(),
          sampleSize: 0,
          recentScores: []
        };
      }

      const rollingAccuracy = userProgress.performance.rollingAccuracy[conceptId];
      rollingAccuracy.recentScores.push(correct ? 1 : 0);
      
      // Keep only last 10 scores for rolling accuracy
      if (rollingAccuracy.recentScores.length > 10) {
        rollingAccuracy.recentScores.shift();
      }
      
      rollingAccuracy.accuracy = rollingAccuracy.recentScores.reduce((sum, score) => sum + score, 0) / rollingAccuracy.recentScores.length;
      rollingAccuracy.lastUpdated = new Date();
      rollingAccuracy.sampleSize = rollingAccuracy.recentScores.length;

      // Update weak/strong concepts
      this.updateConceptStrength(userProgress, conceptId, rollingAccuracy.accuracy);

      // Save to Firestore
      await updateDoc(userProgressRef, {
        mastery: userProgress.mastery,
        'performance.rollingAccuracy': userProgress.performance.rollingAccuracy,
        'performance.weakConcepts': userProgress.performance.weakConcepts,
        'performance.strongConcepts': userProgress.performance.strongConcepts
      });

      // Log review event
      await this.logReviewEvent(userId, conceptId, reviewResult, latencyMs, correct);

    } catch (error) {
      console.error('Error updating mastery:', error);
      throw error;
    }
  }

  /**
   * Update concept strength (weak/strong) based on accuracy
   * @param {Object} userProgress - User progress data
   * @param {string} conceptId - Concept ID
   * @param {number} accuracy - Rolling accuracy (0-1)
   */
  updateConceptStrength(userProgress, conceptId, accuracy) {
    const weakConcepts = userProgress.performance.weakConcepts || [];
    const strongConcepts = userProgress.performance.strongConcepts || [];

    // Remove from both arrays first
    const weakIndex = weakConcepts.indexOf(conceptId);
    const strongIndex = strongConcepts.indexOf(conceptId);
    
    if (weakIndex > -1) weakConcepts.splice(weakIndex, 1);
    if (strongIndex > -1) strongConcepts.splice(strongIndex, 1);

    // Add to appropriate array
    if (accuracy < 0.6) {
      weakConcepts.push(conceptId);
    } else if (accuracy > 0.8) {
      strongConcepts.push(conceptId);
    }

    userProgress.performance.weakConcepts = weakConcepts;
    userProgress.performance.strongConcepts = strongConcepts;
  }

  /**
   * Log review event for analytics
   * @param {string} userId - User ID
   * @param {string} conceptId - Concept ID
   * @param {Object} reviewResult - SRS result
   * @param {number} latencyMs - Response time
   * @param {boolean} correct - Was correct?
   */
  async logReviewEvent(userId, conceptId, reviewResult, latencyMs, correct) {
    try {
      const eventData = {
        userId: userId,
        conceptId: conceptId,
        timestamp: new Date(),
        grade: reviewResult.grade,
        latency: latencyMs,
        correct: correct,
        beforeState: {
          ease: reviewResult.ease,
          intervalDays: reviewResult.intervalDays,
          reps: reviewResult.reps
        },
        afterState: {
          ease: reviewResult.ease,
          intervalDays: reviewResult.intervalDays,
          reps: reviewResult.reps
        },
        reviewType: reviewResult.reps === 0 ? 'new' : 'review'
      };

      await addDoc(collection(db, 'reviewEvents'), eventData);
    } catch (error) {
      console.error('Error logging review event:', error);
    }
  }

  /**
   * Get concept difficulty for prior adjustment
   * @param {string} conceptId - Concept ID
   * @returns {string} Difficulty level
   */
  getConceptDifficulty(conceptId) {
    // This would typically come from the learning node data
    // For now, return default
    return 'medium';
  }

  /**
   * Calculate review load (how many reviews are due)
   * @param {Object} userMastery - User's mastery data
   * @returns {Object} Review load statistics
   */
  getReviewLoad(userMastery) {
    const now = new Date();
    const dueReviews = this.getDueReviews(userMastery, 1000); // Get all due reviews
    
    const totalReviews = Object.keys(userMastery).length;
    const overdueReviews = dueReviews.filter(review => review.daysOverdue > 0);
    const dueToday = dueReviews.filter(review => review.daysOverdue === 0);
    
    return {
      totalConcepts: totalReviews,
      dueReviews: dueReviews.length,
      overdueReviews: overdueReviews.length,
      dueToday: dueToday.length,
      averageOverdue: overdueReviews.length > 0 
        ? overdueReviews.reduce((sum, review) => sum + review.daysOverdue, 0) / overdueReviews.length 
        : 0
    };
  }
}

export default new SRSService();
